/**
 * One account, with its live ingest state.
 *
 * The card is the only place the user learns whether the panel is still good,
 * so status, expiry and last-ingest time are always visible rather than hidden
 * behind a details view.
 */

import { useState } from 'react';
import { IMPORT_SCOPE_LABEL, type AccountStatus, type AccountSummary, type Kind, type PanelAccount } from '@sflx/shared';
import { accounts as accountsApi } from '../lib/api';
import { formatCount, formatDate, formatRelative } from '../lib/format';
import { playlistWatchPath } from '../lib/playlist-path';
import { IngestBanner, isRunningJob as isRunning, KIND_LABEL } from './IngestBanner';
import { useIngestProgress } from '../hooks/useIngestProgress';
import { Link } from '../router/router';
import { ConfirmDialog } from './ConfirmDialog';
import { ErrorNotice, Spinner } from './Feedback';
import { Icon } from './Icon';

interface AccountCardProps {
  account: AccountSummary;
  writable?: boolean;
  onChanged: () => void;
  onEdit: () => void;
}

export function AccountCard({ account, writable = true, onChanged, onEdit }: AccountCardProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [panel, setPanel] = useState<PanelAccount | null>(null);
  const [confirming, setConfirming] = useState(false);

  const progress = useIngestProgress(account.id, onChanged);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());

  const errorJobs = progress.jobs.filter((job) => job.status === 'error' && !dismissed.has(job.jobId));

  const run = async (name: string, action: () => Promise<unknown>) => {
    setBusy(name);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  };

  const refresh = () =>
    run('refresh', async () => {
      await accountsApi.refresh(account.id);
      progress.refresh();
    });

  const check = () =>
    run('check', async () => {
      const result = await accountsApi.check(account.id);
      // The route returns the panel's own verdict verbatim; it is typed
      // `unknown` there only because the shape crosses the wire.
      setPanel(result.panel as PanelAccount);
      onChanged();
    });

  const remove = () =>
    run('delete', async () => {
      setConfirming(false);
      await accountsApi.remove(account.id);
      onChanged();
    });

  const dismissJob = (jobId: string) => {
    setDismissed((current) => new Set([...current, jobId]));
    void accountsApi.dismissJob(account.id, jobId).catch(() => {
      setDismissed((current) => {
        const next = new Set(current);
        next.delete(jobId);
        return next;
      });
    });
  };

  const dismissAllFailed = () => {
    for (const job of errorJobs) dismissJob(job.jobId);
  };

  const host = hostOf(account.serverUrl);
  const kinds: Kind[] = ['live', 'movie', 'series'];
  const enabled = kinds.filter((kind) => isEnabled(account, kind));

  return (
    <article className="card account-card">
      <div className="account-card__top">
        <div className="stack stack--tight" style={{ gap: 'var(--s-1)', minWidth: 0 }}>
          <div className="row" style={{ gap: 'var(--s-3)' }}>
            <h2>{account.label}</h2>
            <StatusBadge status={account.status} />
          </div>
          <div className="account-card__meta">
            <span className="mono">{host}</span>
            {account.importScope ? (
              <span>{IMPORT_SCOPE_LABEL[account.importScope]}</span>
            ) : (
              <span>{account.username}</span>
            )}
            {account.importScope ? null : <span>Expires {formatDate(account.expiresAt)}</span>}
            {account.importScope ? null : <span>Last fetch {formatRelative(account.lastIngestAt)}</span>}
            {account.maxConnections ? <span>{account.maxConnections} connections</span> : null}
          </div>
        </div>

        <div className="row">
          {account.importScope ? (
            <Link className="btn btn--sm" to={playlistWatchPath(account.importScope, account.id)}>
              <Icon name="tv" size={15} />
              Watch
            </Link>
          ) : null}
          {writable && !account.importScope ? (
            <>
              <button type="button" className="btn btn--sm" onClick={refresh} disabled={busy !== null}>
                {busy === 'refresh' ? <Spinner /> : <Icon name="refresh" size={15} />}
                Refresh
              </button>
              <button type="button" className="btn btn--sm" onClick={check} disabled={busy !== null}>
                {busy === 'check' ? <Spinner /> : <Icon name="check" size={15} />}
                Check
              </button>
            </>
          ) : null}
          {writable ? (
            <>
              <button type="button" className="btn btn--sm" onClick={onEdit} disabled={busy !== null}>
                <Icon name="pencil" size={15} />
                Edit
              </button>
              <button
                type="button"
                className="btn btn--sm btn--danger"
                onClick={() => setConfirming(true)}
                disabled={busy !== null}
              >
                <Icon name="trash" size={15} />
                Delete
              </button>
            </>
          ) : null}
        </div>
      </div>

      {enabled.length ? (
        <div className="account-card__counts">
          {enabled.map((kind) => (
            <div className="count" key={kind}>
              <span className="count__value">{formatCount(account.counts[kind] ?? 0)}</span>
              <span className="count__label">{KIND_LABEL[kind]}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          {account.importScope
            ? 'Public playlist. Open Watch to see which channels play.'
            : 'Nothing is selected for this account yet. Use Edit to tick Live, Movies or TV Series.'}
        </p>
      )}

      {writable && account.ingest.live ? (
        <div className="row">
          <Link className="btn btn--sm" to={`/accounts/${account.id}/countries`}>
            <Icon name="globe" size={15} />
            {account.liveCountryCodes?.length
              ? `Countries: ${account.liveCountryCodes.join(', ')}${account.includeOther ? ' + Other' : ''}`
              : 'Choose countries'}
          </Link>
        </div>
      ) : null}

      {writable
        ? progress.jobs.filter(isRunning).map((job) => (
            <IngestBanner key={job.jobId} job={job} account={account} />
          ))
        : null}

      {writable && errorJobs.length > 1 ? (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn--sm btn--ghost" onClick={dismissAllFailed}>
            Dismiss all
          </button>
        </div>
      ) : null}

      {writable
        ? errorJobs.map((job) => (
            <div className="notice notice--error" key={job.jobId}>
              <div className="notice__head">
                <div className="notice__title">{KIND_LABEL[job.kind]} fetch failed</div>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  onClick={() => dismissJob(job.jobId)}
                >
                  Dismiss
                </button>
              </div>
              <div className="notice__detail">{job.error ?? 'The panel did not finish the job.'}</div>
            </div>
          ))
        : null}

      {panel ? (
        <div className="notice">
          <div className="notice__title">Panel replied: {panel.status}</div>
          <div className="notice__detail">
            {panel.message ? `${panel.message} · ` : ''}
            Expires {formatDate(panel.expiresAt)}
            {panel.activeConnections !== null && panel.maxConnections !== null
              ? ` · ${panel.activeConnections} of ${panel.maxConnections} connections in use`
              : ''}
            {panel.allowedFormats?.length ? ` · formats: ${panel.allowedFormats.join(', ')}` : ''}
          </div>
        </div>
      ) : null}

      <ErrorNotice error={error} />

      {confirming ? (
        <ConfirmDialog
          title={`Delete ${account.label}?`}
          body={
            account.importScope
              ? 'This public playlist is removed from your playlists. Channels you already tested in this browser stay in this tab until you clear them.'
              : 'The account and everything fetched from it are removed. Favorites and resume positions go with it.'
          }
          confirmLabel={account.importScope ? 'Delete playlist' : 'Delete account'}
          destructive
          onConfirm={() => void remove()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </article>
  );
}


function StatusBadge({ status }: { status: AccountStatus }) {
  const tone =
    status === 'active' ? 'badge--ok' : status === 'unknown' ? '' : status === 'expired' ? 'badge--warn' : 'badge--danger';
  return <span className={`badge ${tone}`}>{status}</span>;
}

function isEnabled(account: AccountSummary, kind: Kind): boolean {
  return kind === 'live' ? account.ingest.live : kind === 'movie' ? account.ingest.movies : account.ingest.series;
}

function hostOf(serverUrl: string): string {
  try {
    return new URL(serverUrl).host;
  } catch {
    return serverUrl;
  }
}
